import { Link } from "react-router-dom";
import { Leaf, ShieldCheck, Star, Truck, BadgeCheck, HeartHandshake } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Us | Farmacy District DC"
        description="Farmacy District DC is Washington DC's trusted cannabis dispensary — premium products, competitive prices, and fast, discreet delivery across the District."
      />
      <section className="relative bg-brand-ink py-24 text-white grain sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">About Farmacy District</p>
            <h1 className="mt-4 max-w-2xl font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              Your Trusted Cannabis Dispensary in Washington, DC
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
              Farmacy District DC is your local cannabis dispensary, providing fast, reliable, and discreet delivery
              throughout the District of Columbia.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Quality You Can Count On</h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-600">
              We're proud to offer one of the widest selections of premium cannabis products in the District —
              top-shelf flowers, edibles, concentrates, pre-rolls, and specialty items like Shatter and Moonrocks.
              Whether you're a seasoned enthusiast or exploring cannabis for the first time, our carefully curated
              menu makes it easy to find exactly what you're looking for.
            </p>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              Every product is carefully sourced and verified, so you can shop with confidence — and our delivery
              network gets it to you fast, with clear communication every step of the way.
            </p>
            <div className="mt-8 flex items-center gap-3 border border-neutral-200 bg-white p-5">
              <Star className="h-8 w-8 fill-brand-lime text-brand-lime" />
              <div>
                <p className="font-display text-xl font-black">4.9 / 5</p>
                <p className="text-xs text-neutral-500">Based on 1,251 Google reviews</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden">
              <img
                src="https://images.pexels.com/photos/33349199/pexels-photo-33349199.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Premium cannabis flower held by a gloved hand"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover sm:aspect-[4/3]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-sand py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">With Farmacy District, You Can…</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {[
              { icon: Leaf, text: "Explore a wide selection of high-quality cannabis products curated for every preference." },
              { icon: BadgeCheck, text: "Discover trusted flower, edibles, vapes, and concentrates at competitive prices." },
              { icon: Truck, text: "Rely on fast, discreet delivery with clear communication every step of the way." },
              { icon: ShieldCheck, text: "Shop with confidence knowing all products are carefully sourced and verified." },
              { icon: HeartHandshake, text: "Enjoy a smooth, stress-free ordering experience from start to finish." },
              { icon: Star, text: "Join a growing community that values quality, consistency, and customer care." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex h-full items-start gap-4 border border-neutral-200 bg-white p-6" data-testid={`about-value-${i}`}>
                  <f.icon className="h-6 w-6 flex-shrink-0 text-brand-red" />
                  <p className="text-sm leading-relaxed text-neutral-600">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24" id="contact">
        <Reveal>
          <div className="bg-brand-ink p-10 text-white grain sm:p-14">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Let's Talk</h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-400">
                  Questions about products, delivery or your order? Our team is here for you.
                </p>
              </div>
              <div className="space-y-3 text-sm">
                <a href="tel:+12029134523" className="block font-bold text-brand-lime" data-testid="about-phone">(202) 913-4523</a>
                <a href="mailto:Info@farmacydistrictdc.com" className="block font-bold text-brand-lime" data-testid="about-email">Info@farmacydistrictdc.com</a>
                <p className="text-neutral-400">1100 New Jersey Ave SE, Washington, DC</p>
                <Link to="/shop" className="mt-4 inline-block rounded-full bg-brand-lime px-8 py-3.5 text-sm font-bold text-brand-ink transition-colors hover:bg-brand-limehover" data-testid="about-shop-cta">
                  SHOP NOW
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
